// pricing.js
import {
  calculateTotalWeight,
  calculateTotalVolume,
  checkDimensionFit,
} from "./pricing/utils.js";
import {
  POSTAL_TIERS,
  SIZE_TIERS,
  MXL_SETTINGS,
  MH_SETTINGS,
} from "./pricing/config.js";
import { calculatePrice } from "./pricing/functions/price.js";
import { majorStations } from "./pricing/data/stations.js";
import { calculateDistance } from "./pricing/functions/postalCode.js";
import { getStationByPostalCode } from "./pricing/functions/postalCode.js";

function normalizePostalCode(pc) {
  if (!pc && pc !== 0) return null;
  const s = String(pc).trim();
  return s ? s.padStart(5, "0") : null;
}

function hasCoords(loc) {
  return (
    loc && typeof loc.latitude === "number" && typeof loc.longitude === "number"
  );
}

function coordinatesToPostalCode(lat, lng) {
  let best = null;
  let bestKm = Infinity;

  for (const st of majorStations) {
    const stLat = st.lat || st.centerLat;
    const stLng = st.lng || st.centerLng;
    if (typeof stLat !== "number" || typeof stLng !== "number") continue;

    const km = calculateDistance(lat, lng, stLat, stLng);
    if (km < bestKm) {
      bestKm = km;
      best = st.postalCode;
    }
  }

  return normalizePostalCode(best);
}

function resolvePostalCode(location, fallback = null) {
  if (!location) return fallback;

  const direct = normalizePostalCode(location.postal_code);
  if (direct) return direct;

  if (hasCoords(location)) {
    const derived = coordinatesToPostalCode(
      location.latitude,
      location.longitude
    );
    if (derived) return derived;
  }

  return fallback;
}

function getWeightMultiplier(weightGrams, tierSizeKg = 19) {
  const weightKg = weightGrams / 1000;
  return Math.ceil(weightKg / tierSizeKg) || 1;
}

function getPostalCodeTier(destination) {
  if (!destination || !destination.postal_code) {
    return null;
  }

  const postalCode = destination.postal_code.toString().padStart(5, "0");

  if (
    POSTAL_TIERS.tier1PostalRanges.some(
      (range) => postalCode >= range.min && postalCode <= range.max
    )
  ) {
    return 1;
  }

  if (
    POSTAL_TIERS.tier2PostalRanges.some(
      (range) => postalCode >= range.min && postalCode <= range.max
    )
  ) {
    return 2;
  }

  if (
    POSTAL_TIERS.tier3PostalRanges.some(
      (range) => postalCode >= range.min && postalCode <= range.max
    )
  ) {
    return 3;
  }

  return 4;
}

function calculateItemDimensions(item) {
  if (item.length && item.width && item.height) {
    return {
      length: item.length,
      width: item.width,
      height: item.height,
    };
  }

  return { length: 20, width: 40, height: 55 };
}

function findBestSizeTier(items) {
  let maxLength = 0;
  let maxWidth = 0;
  let maxHeight = 0;

  items.forEach((item) => {
    const quantity = item.quantity || 1;
    const dimensions = calculateItemDimensions(item);

    maxLength = Math.max(
      maxLength,
      dimensions.length * Math.ceil(Math.sqrt(quantity))
    );
    maxWidth = Math.max(maxWidth, dimensions.width);
    maxHeight = Math.max(maxHeight, dimensions.height);
  });

  for (const tier of SIZE_TIERS) {
    const { length, width, height } = tier.max_dimensions;
    if (maxLength <= length && maxWidth <= width && maxHeight <= height) {
      return tier;
    }
  }

  return null;
}

function getNextAvailableDeliveryDayPlusOne(deliverySettings) {
  const now = new Date();
  let nextDay = new Date(now);
  nextDay.setDate(nextDay.getDate() + 1);

  const dayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  for (let i = 0; i < 7; i++) {
    const dayKey = dayNames[nextDay.getDay()];
    if (deliverySettings.enabledDays[dayKey]) {
      nextDay.setDate(nextDay.getDate() + 1);
      break;
    }
    nextDay.setDate(nextDay.getDate() + 1);
  }

  for (let i = 0; i < 7; i++) {
    const dayKey = dayNames[nextDay.getDay()];
    if (deliverySettings.enabledDays[dayKey]) {
      const finnishDays = [
        "sunnuntaina",
        "maanantaina",
        "tiistaina",
        "keskiviikkona",
        "torstaina",
        "perjantaina",
        "lauantaina",
      ];
      return finnishDays[nextDay.getDay()];
    }
    nextDay.setDate(nextDay.getDate() + 1);
  }

  return "";
}

function getNextAvailableDeliveryDay(deliverySettings) {
  const now = new Date();
  let nextDay = new Date(now);
  nextDay.setDate(nextDay.getDate() + 1);

  const dayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  for (let i = 0; i < 7; i++) {
    const dayKey = dayNames[nextDay.getDay()];
    if (deliverySettings.enabledDays[dayKey]) {
      const finnishDays = [
        "sunnuntaina",
        "maanantaina",
        "tiistaina",
        "keskiviikkona",
        "torstaina",
        "perjantaina",
        "lauantaina",
      ];
      return finnishDays[nextDay.getDay()];
    }
    nextDay.setDate(nextDay.getDate() + 1);
  }

  return "";
}

function isSameTier(origin, destination) {
  const originNearestStation = getPostalCodeTier(origin);
  const destinationNearestStation = getPostalCodeTier(destination);
  return originNearestStation === destinationNearestStation;
}

const formatDeliveryTime = (addMinutes) => {
  const deliveryTime = new Date(Date.now() + addMinutes * 60 * 1000);
  return deliveryTime.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatTimestamp = (ms) =>
  ms
    ? new Date(ms).toLocaleTimeString("fi-FI", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

export function calculateRates(rateRequest) {
  console.log("rateRequest", rateRequest);
  const {
    items,
    currency = "EUR",
    pickupConfig,
    enabledOptions,
    durationMin = 0,
    km = 0,
    pickupTime,
    targetTime,
    hasTimeWindow,
  } = rateRequest;

  // ✅ HARD REQUIREMENT
  if (!hasCoords(rateRequest?.origin) || !hasCoords(rateRequest?.destination)) {
    return {
      rates: [],
      debug: { reason: "MISSING_COORDINATES" },
    };
  }

  const totalWeightGrams = calculateTotalWeight(items);
  const totalVolume = calculateTotalVolume(items);

  const distanceInKm = km / 1000

  const hasLessThanTwoHours = targetTime && pickupTime && targetTime - pickupTime < 2 * 60 * 60 * 1000;
  let expressPrice =  Math.round(Math.max(20, distanceInKm * 1.0) * 100) / 100;
  // this can be tought
  if(hasLessThanTwoHours) {
    expressPrice += 15;
  }

  const rates = [];


  // this needs some thinking
  // ── Express vs Custom (time window) ──────────────────────────────────────
  if ((hasTimeWindow && (pickupTime || targetTime) || pickupTime > Date.now())) {
    const pickupLabel = formatTimestamp(pickupTime);
    const targetLabel = formatTimestamp(targetTime);

    const customPrice = hasTimeWindow ? expressPrice + 4 : expressPrice;
    const timeDesc =
      pickupLabel && targetLabel
        ? `Nouto ${pickupLabel} → toimitus ${targetLabel}`
        : pickupLabel
        ? `Nouto klo. ${pickupLabel}`
        : `Toimitus klo. ${targetLabel}`;

    rates.push({
      id: "express",
      title: "Express",
      description: timeDesc,
      priceType: "fixed",
      price: customPrice,
    });
  } else {
    rates.push({
      id: "express",
      title: "Express",
      description: `Toimitus mahdollinen, klo. ${formatDeliveryTime(Math.max(60, durationMin * 1.2))}`,
      priceType: "fixed",
      price: expressPrice,
    });
  }

  const ecoOptions = [];
  const now = new Date();

  // ✅ Resolve postal codes early (coords -> nearest supported station)
  const originPostalCode = resolvePostalCode(rateRequest?.origin, "00100");
  const destinationPostalCode = resolvePostalCode(
    rateRequest?.destination,
    null
  );

  const origin = { postal_code: originPostalCode };
  const destination = { postal_code: destinationPostalCode };

  const originTier = getPostalCodeTier(origin);
  const destinationTier = getPostalCodeTier(destination);

  const withinCity = isSameTier(origin, destination);
  const intraCity = km > 80;

  const SAME_DAY_SETTINGS = pickupConfig
    ? {
        ...MXL_SETTINGS,
        ...pickupConfig,
      }
    : MXL_SETTINGS;

  const dimensionsFitMyXline = checkDimensionFit(
    totalVolume,
    SAME_DAY_SETTINGS.maxDimensions
  );

  const myxlineCheck =
    totalWeightGrams <= SAME_DAY_SETTINGS.weightRestriction &&
    dimensionsFitMyXline &&
    originTier !== null;

  const myXDoorDelivery = enabledOptions.includes("FD");
  const myxLockerDelivery = enabledOptions.includes("FP");
  const myXlineEnabled = true;

  if (myxlineCheck && myXlineEnabled) {
    let basePrice = 9.9;
    if (destinationTier === 2) {
      basePrice += SAME_DAY_SETTINGS.tierSurcharges.tier2;
    } else if (destinationTier === 3) {
      basePrice += SAME_DAY_SETTINGS.tierSurcharges.tier3;
    }

    let earliestDelivery;
    let deliveryDate;

    if (destinationTier !== null) {
      const dayKey = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ][now.getDay()];

      const isEnabledToday = SAME_DAY_SETTINGS.enabledDays[dayKey];

      const [cutoffHour, cutoffMinute] =
        SAME_DAY_SETTINGS.cutoffTime.split(/[:\s]/);
      const cutoffAmPm = SAME_DAY_SETTINGS.cutoffTime.split(" ")[1];
      let cutoffHour24 = parseInt(cutoffHour);
      if (cutoffAmPm === "PM" && cutoffHour24 !== 12) cutoffHour24 += 12;
      if (cutoffAmPm === "AM" && cutoffHour24 === 12) cutoffHour24 = 0;

      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffHour24, parseInt(cutoffMinute), 0, 0);
      const durationInMillis = durationMin * 60 * 1000;
      const nowPlusDuration = new Date(now.getTime() + durationInMillis);
      const isBeforeCutoff = nowPlusDuration < cutoffTime;

      earliestDelivery =
        isBeforeCutoff && isEnabledToday
          ? "tänään"
          : getNextAvailableDeliveryDay(SAME_DAY_SETTINGS);
      deliveryDate = now.toISOString().split("T")[0];

      const estimatedPrice = calculatePrice(
        originPostalCode,
        destinationPostalCode
      );
      const weightMultiplier = getWeightMultiplier(totalWeightGrams, 19);
      console.log("weightMultiplier", weightMultiplier);
      let price = (basePrice + estimatedPrice) * weightMultiplier;
      console.log("intraCity", intraCity);

      // Only push fast if no time window active
      if (!hasTimeWindow && (pickupTime <= Date.now())) {
        if (intraCity) {
          rates.push({
            id: "fast",
            price: price,
            title: "Pika",
            service_name: `MyXline - Pikakuljetus ⚡`,
            service_code: "SAME_DAY_HOME_DELIVERY",
            description: `Toimitus ${earliestDelivery}, klo. 18-22\n`,
            total_price: Math.round(price * 100),
            currency: currency,
          });
        } else {
          rates.push({
            id: "fast",
            price: expressPrice * 0.8,
            title: "Pika",
            service_name: `MyXline - Pikakuljetus ⚡`,
            service_code: "SAME_DAY_HOME_DELIVERY",
            description: `Toimitus ${earliestDelivery}, klo. 18-22\n`,
            total_price: Math.round(price * 100),
            currency: currency,
          });
        }
      }
    } else {
      earliestDelivery = getNextAvailableDeliveryDay(SAME_DAY_SETTINGS);
      const nextDay = new Date(now);
      nextDay.setDate(nextDay.getDate() + 1);
      deliveryDate = nextDay.toISOString().split("T")[0];
    }

    const closestSupportedLocation =
      getStationByPostalCode(destination.postal_code)?.postalCode || "00100";
    const pickupStation =
      getStationByPostalCode(closestSupportedLocation)?.name || "";

    if (intraCity) {
      ecoOptions.push({
        id: "bus",
        title: "Bussipaketti",
        price: basePrice,
        service_name: `Pikakuljetus - noutopiste`,
        service_code: "SAME_DAY_PICKUP",
        description: `Voit hakea sopivimman bussilinjan kuljetuksellesi`,
        descrptionOld: ` Aikaisin mahdollinen toimitus ${earliestDelivery}, klo. 18-22, ${pickupStation}. Saat viestin, josta voit seurata lähetyksen etenemistä.`,
        total_price: Math.round(basePrice * 100),
        currency: currency,
      });
    }
  }

  // MatkaHuolto rates (basic delivery, always +1 day)
  const dimensionsFit = checkDimensionFit(
    totalVolume,
    MH_SETTINGS.maxDimensions
  );

  const earliestDelivery = getNextAvailableDeliveryDayPlusOne(MH_SETTINGS);
  const nextDay = new Date(earliestDelivery);
  nextDay.setDate(nextDay.getDate() + 1);
  const mhPrice = findBestSizeTier(items).base_price;

  if (totalWeightGrams <= MH_SETTINGS.weightRestriction && dimensionsFit) {
    ecoOptions.push({
      id: "regular",
      title: "Postipaketti",
      price: mhPrice,
      service_name: `Peruskuljetus - kotiinkuljetus`,
      service_code: "BASIC_HOME_DELIVERY",
      description: `Aikaisin mahdollinen toimitus ${earliestDelivery}, klo. 18-22`,
      total_price: Math.round(mhPrice * 100),
      currency: currency,
    });
  }

  ecoOptions.push({
    id: "bid",
    title: "Kuljetustarjous",
    price: mhPrice - 5,
    service_name: `Peruskuljetus - noutopiste`,
    service_code: "BASIC_PICKUP",
    description: `Voit tarjota kuljetukselle omavalintaisen hinnan.`,
    total_price: Math.round((mhPrice - 5) * 100),
    currency: currency,
  });

  rates.push({
    id: "eco",
    title: "Eko",
    description: intraCity
      ? "Edulliset ja ympäristöystävälliset vaihtoehdot, esimerkiksi bussi -ja postipaketti"
      : "Edulliset ja ympäristöystävälliset vaihtoehdot, esimerkiksi kuljetustarjous",
    priceType: "startingFrom",
    price: 3,
    options: ecoOptions,
  });
  return {
    rates: rates,
    debug: {
      totalWeight: totalWeightGrams,
      totalVolume: totalVolume,
      destinationTier: destinationTier,
      withinCity: withinCity,
      mxlEligible: myxlineCheck,
      mhEligible:
        totalWeightGrams <= MH_SETTINGS.weightRestriction && dimensionsFit,
      dimensionsFit: dimensionsFit,
    },
  };
}